import React, { useState } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaUserPlus,
  FaTrash,
  FaSearch,
  FaSyncAlt,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { toast } from "react-toastify";
import BatchService from "../../../../services/batchService";
import AddStudentModal from "./AddStudentModal";
import ExportButton from "../../../../components/common/ExportButton";

const BatchStudentsTab = ({
  batchId,
  students,
  availableStudents,
  onStudentAdded,
  onStudentRemoved,
  onProgressUpdated,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingProgress, setUpdatingProgress] = useState(false);
  const [updatingManualProgress, setUpdatingManualProgress] = useState({});

  const filteredStudents = students.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleRefreshProgress = async () => {
    try {
      setUpdatingProgress(true);
      await BatchService.updateAutoProgress(batchId);
      toast.success("Progress updated automatically based on batch dates");
      onProgressUpdated();
    } catch (error) {
      toast.error("Failed to update progress");
    } finally {
      setUpdatingProgress(false);
    }
  };

  const handleRemoveStudent = async () => {
    try {
      await BatchService.removeStudentFromBatch(batchId, selectedStudent.id);
      toast.success("Student removed from batch");
      onStudentRemoved();
      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Failed to remove student");
    }
  };

  const handleExportBatchStudents = async () => {
    await BatchService.exportBatchStudents(batchId);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge bg="success">Active</Badge>;
      case "completed":
        return <Badge bg="info">Completed</Badge>;
      case "dropped":
        return <Badge bg="danger">Dropped</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return "bg-success";
    if (progress >= 50) return "bg-info";
    if (progress >= 25) return "bg-warning";
    return "bg-secondary";
  };

  const handleManualProgress = async (studentId, increment) => {
    setUpdatingManualProgress((prev) => ({ ...prev, [studentId]: true }));
    try {
      await BatchService.updateStudentProgressManual(
        batchId,
        studentId,
        increment,
      );
      toast.success(
        `Progress ${increment > 0 ? "increased" : "decreased"} by ${Math.abs(increment)}%`,
      );
      onProgressUpdated();
    } catch (error) {
      toast.error("Failed to update progress");
    } finally {
      setUpdatingManualProgress((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const handleRecalculateProgress = async () => {
    try {
      setUpdatingProgress(true);
      await BatchService.recalculateProgress(batchId);
      toast.success("Progress recalculated based on current materials");
      onProgressUpdated();
    } catch (error) {
      toast.error("Failed to recalculate progress");
    } finally {
      setUpdatingProgress(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Batch Students</h5>
        <div>
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowAddModal(true)}
          >
            <FaUserPlus className="me-2" /> Add Student
          </Button>
          &nbsp;&nbsp;
          <Button
            variant="warning"
            size="sm"
            className="me-2"
            onClick={handleRecalculateProgress}
            disabled={updatingProgress}
          >
            <FaSyncAlt
              className={`me-2 ${updatingProgress ? "fa-spin" : ""}`}
            />
            Recalculate Progress
          </Button>
          &nbsp;&nbsp;
          <ExportButton
            onExport={handleExportBatchStudents}
            label="Export List"
            size="sm"
            variant="info"
          />
        </div>
      </div>

      {/* Search */}
      <InputGroup className="mb-3">
        <InputGroup.Text>
          <FaSearch />
        </InputGroup.Text>
        <Form.Control
          placeholder="Search students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InputGroup>

      {/* Students Table */}
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>Student Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Joined Date</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredStudents.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-4">
                No students in this batch
              </td>
            </tr>
          ) : (
            filteredStudents.map((student) => (
              <tr key={student.id}>
                <td>
                  <strong>{student.name}</strong>
                </td>
                <td>{student.email}</td>
                <td>{student.phone}</td>
                <td>{new Date(student.joined_date).toLocaleDateString()}</td>
                <td>{getStatusBadge(student.status)}</td>
                <td>
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <div
                        className="progress flex-grow-1 me-2"
                        style={{ height: "8px" }}
                      >
                        <div
                          className={`progress-bar ${getProgressColor(student.progress || 0)}`}
                          style={{ width: `${student.progress || 0}%` }}
                        />
                      </div>
                      <small className="fw-bold">
                        {student.progress || 0}%
                      </small>
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                      <small
                        className="text-muted"
                        style={{ fontSize: "10px" }}
                      >
                        📹 Auto: {student.auto_progress || 0}%
                      </small>
                      <small
                        className="text-muted"
                        style={{ fontSize: "10px" }}
                      >
                        ✋ Manual: {student.manual_progress || 0}%
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleManualProgress(student.id, 5)}
                      disabled={updatingManualProgress[student.id]}
                      title="Add 5% progress"
                    >
                      <FaPlus />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleManualProgress(student.id, -5)}
                      disabled={updatingManualProgress[student.id]}
                      title="Remove 5% progress"
                    >
                      <FaMinus />
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => {
                        setSelectedStudent(student);
                        setShowDeleteModal(true);
                      }}
                      title="Remove from Batch"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Add Student Modal */}
      <AddStudentModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        batchId={batchId}
        availableStudents={availableStudents}
        onStudentAdded={onStudentAdded}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirm Remove</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <p>
              Are you sure you want to remove{" "}
              <strong>{selectedStudent.name}</strong> from this batch?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemoveStudent}>
            Remove Student
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BatchStudentsTab;
